// Single source of truth for the base map tile layer, used by every page
// that renders a Leaflet map (Operational Center, Fuel Map, Route History,
// Fleet Dashboard).
//
// Tiles are fetched through the backend's own tile-cache proxy
// (routers/tile_proxy.py), not directly from HERE. That module:
//   - keeps the real HERE API key server-side only (never shipped to the
//     browser -- previously it was baked straight into this file's build
//     output, protected only by a referrer restriction)
//   - caches every tile on disk, so 10 dispatchers panning/zooming
//     overlapping highway corridors mostly hit a free cache instead of a
//     billable HERE transaction
//   - enforces a hard monthly cost ceiling (here.monthly_tile_cost_cap_usd,
//     $150/month by default) -- once this month's logged tile spend hits
//     that cap, the backend transparently starts serving OSM tiles
//     instead, so HERE tile spend can structurally never exceed it and the
//     map never goes blank
//
// Leaflet's TileLayer issues plain <img> GETs (no custom headers), so
// per-request auth can't use the normal Authorization bearer header --
// useTileLayerUrl() fetches a short-lived (1hr), narrowly-scoped tile
// token once (via a real authenticated call) and embeds it as a query
// param instead of the long-lived session token.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

export const TILE_LAYER_ATTRIBUTION =
  '&copy; <a href="https://legal.here.com/terms">HERE</a> | ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Refresh the token 5 minutes before it actually expires, not exactly at
// expiry -- a network hiccup right at the boundary shouldn't cost a
// dispatcher a blank map while a fresh token round-trips.
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

// Starts as null (caller should skip rendering <TileLayer> until this is
// truthy) rather than an instant OSM placeholder -- Leaflet fully reloads
// EVERY visible tile whenever a TileLayer's url prop changes, so starting
// with a placeholder and swapping to the real proxied URL a moment later
// was doubling every page's initial tile load (fetch once from OSM, then
// immediately again from the proxy). A one-time async round trip for the
// token is cheaper than that.
export function useTileLayerUrl() {
  const [tileUrl, setTileUrl] = useState(null);
  const refreshTimer = useRef(null);

  const fetchToken = useCallback(() => {
    const authToken = localStorage.getItem("token");
    if (!authToken) return;
    axios
      .get(`${apiBaseUrl}/tiles/token`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        const { token, expires_in_seconds } = res.data;
        setTileUrl(`${apiBaseUrl}/tiles/{z}/{x}/{y}.png?t=${encodeURIComponent(token)}`);
        const nextInMs = Math.max(expires_in_seconds * 1000 - TOKEN_REFRESH_MARGIN_MS, 30000);
        refreshTimer.current = setTimeout(fetchToken, nextInMs);
      })
      .catch(() => {
        // Leave whatever URL is currently set (null, or the last-good
        // proxied one on a refresh failure) -- retry shortly rather than
        // clearing a working map.
        refreshTimer.current = setTimeout(fetchToken, 30000);
      });
  }, []);

  useEffect(() => {
    fetchToken();
    return () => clearTimeout(refreshTimer.current);
  }, [fetchToken]);

  return tileUrl;
}
